import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, List, ListItemAvatar,
  ListItemButton, ListItemText, Tab, Tabs, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';

/** The guest list: who is going and who is interested; the host also sees who was invited and who can't go. */
export default function AttendeesDialog({ open, onClose, event, initialTab = 'going', zIndex }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  useEffect(() => {
    if (!open || !event?.id) return;
    setLoading(true);
    setError(null);
    axiosService.get(`/events/${event.id}/attendees/`, { params: { status: tab } })
      .then(({ data }) => setPeople(data || []))
      .catch((err) => { setPeople([]); setError(apiErrorMessage(err, 'Could not load the guest list.')); })
      .finally(() => setLoading(false));
  }, [open, event?.id, tab]);

  if (!event) return null;
  const tabs = [
    { value: 'going', label: `Going · ${event.going_count || 0}` },
    { value: 'interested', label: `Interested · ${event.interested_count || 0}` },
    ...(event.can_edit ? [{ value: 'invited', label: 'Invited' }, { value: 'declined', label: "Can't go" }] : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={zIndex ? { zIndex } : undefined}>
      <DialogTitle sx={{ pr: 6 }}>
        Guests
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        {tabs.map((t) => <Tab key={t.value} value={t.value} label={t.label} sx={{ textTransform: 'none' }} />)}
      </Tabs>
      <DialogContent sx={{ px: 1, minHeight: 240 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>}
        {!loading && error && <Typography color="error" variant="body2" sx={{ px: 2 }}>{error}</Typography>}
        {!loading && !error && people.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
            Nobody here yet.
          </Typography>
        )}
        {!loading && !error && people.length > 0 && (
          <List dense>
            {people.map((p) => (
              <ListItemButton key={p.id} onClick={() => { onClose(); navigate(`/profile/${p.id}/`); }} sx={{ borderRadius: 1 }}>
                <ListItemAvatar>
                  <Avatar src={p.avatar_url || undefined}>{(p.name || '?')[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <span>{p.name}</span>
                      {p.is_host && <Chip label="Host" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />}
                      {p.is_connection && <Chip label="Connection" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />}
                    </Box>
                  }
                  secondary={p.invited_by && tab === 'invited' ? `Invited by ${p.invited_by}` : p.headline}
                  slotProps={{ secondary: { noWrap: true } }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
